/**
 * Generate a shareable WhatsApp-style text for daily challenge results.
 * Picks randomly from a tone-matched pool of templates.
 */

import type { Stage } from "../audio";
import { BRAND } from "./brand";

export interface ShareData {
  dailyNumber: number;
  stage: Stage | 0;
  score: number;
  guesses: string[];
  maxStages: number;
}

type ShareTone = "triumph" | "great" | "mid" | "fail";

interface ShareTemplate {
  he: string;
  en: string;
}

const TONE_TEMPLATES: Record<ShareTone, ShareTemplate[]> = {
  triumph: [
    {
      he: "♪ ניגונים ♪ משחק יומי של שיר אחד, תו אחד\nזיהיתי כבר בתו הראשון, בחסדי שמיים\nהציון שלי: {score}\n{url}",
      en: "♪ Niggunim ♪ Daily game — one song, one note\nGot it on the first note\nMy score: {score}\n{url}",
    },
    {
      he: "ניגונים — ניחוש בתו הראשון\nהציון שלי היום: {score}\nבואו לנסות גם אתם\n{url}",
      en: "Niggunim — first-note guess\nToday's score: {score}\nCome try it\n{url}",
    },
  ],
  great: [
    {
      he: "ניגונים — המשחק היומי של מוזיקה יהודית\nתו אחד ומנחשים את הניגון\nהציון שלי היום: {score}\n{url}",
      en: "Niggunim — daily Jewish music game\nOne note and you guess the niggun\nMy score today: {score}\n{url}",
    },
    {
      he: "ניסיתם כבר את ניגונים?\nכל יום ניגון אחד, משמיעים תו ואתם מנחשים\nהציון שלי: {score}\n{url}",
      en: "Tried Niggunim yet?\nOne niggun a day — they play a note, you guess\nMy score: {score}\n{url}",
    },
    {
      he: "ניגונים — היה לי יום טוב\nניחשתי בתו ה-{stage}\n{url}",
      en: "Niggunim — good day today\nGot it on note #{stage}\n{url}",
    },
  ],
  mid: [
    {
      he: "ניגונים — המשחק היומי של מוזיקה יהודית\nניחוש מתו אחד, ניגון אחד ביום\nהציון שלי: {score} (לקח {stage} ניחושים)\n{url}",
      en: "Niggunim — daily Jewish music game\nOne note, one niggun a day\nMy score: {score} (took {stage} guesses)\n{url}",
    },
    {
      he: "ממליץ על ניגונים\nכל יום ניגון אחד, משמיעים תו ואתם מנחשים\nהציון של היום: {score}\n{url}",
      en: "I recommend Niggunim\nDaily niggun, one note to guess from\nToday: {score}\n{url}",
    },
  ],
  fail: [
    {
      he: "ניגונים — הניגון היומי הפתיע אותי\nלא עליתי עליו הפעם\n{url}",
      en: "Niggunim — today's niggun caught me off guard\nDidn't get it this time\n{url}",
    },
    {
      he: "ניסיתי את ניגונים היום — לא קל\nניגון אחד, תו אחד, ומנחשים\nבואו לנסות גם אתם\n{url}",
      en: "Tried Niggunim today — not easy\nOne niggun, one note, guess\nYou try it\n{url}",
    },
  ],
};

const GENERIC_TEMPLATES: ShareTemplate[] = [
  {
    he: "♪ ניגונים ♪\nמשחק יומי של מוזיקה יהודית — ניגון אחד ביום, ניחוש מתו אחד\n{url}",
    en: "♪ Niggunim ♪\nDaily Jewish music game — one niggun a day, guess from a single note\n{url}",
  },
  {
    he: "ניגונים — ניגון ליום\nחסידי, חזנות, ניגוני שבת\n{url}",
    en: "Niggunim — a niggun a day\nChassidic, chazzanut, shabbos\n{url}",
  },
  {
    he: "ניגונים — בכל יום ניגון חדש\nתו אחד ומנחשים\nשווה לנסות\n{url}",
    en: "Niggunim — a new niggun every day\nOne note, you guess\nWorth a try\n{url}",
  },
];

function pickTone(stage: Stage | 0): ShareTone {
  if (stage === 0) return "fail";
  if (stage === 1) return "triumph";
  if (stage <= 3) return "great";
  return "mid";
}

export function generateShareUrl(data: ShareData, displayName: string): string {
  const params = new URLSearchParams({
    d: String(data.dailyNumber),
    s: String(data.stage),
    n: displayName || "Someone",
  });
  return `${BRAND.siteUrl}/share?${params.toString()}`;
}

export function generateShareText(
  data: ShareData,
  _streak: number,
  displayName: string = "Someone",
  locale: "he" | "en" = "en"
): string {
  const pool = [...TONE_TEMPLATES[pickTone(data.stage)], ...GENERIC_TEMPLATES];
  const template = pool[Math.floor(Math.random() * pool.length)];
  const raw = locale === "he" ? template.he : template.en;

  const score = `${data.stage === 0 ? data.maxStages : data.stage}/${data.maxStages}`;
  const url = generateShareUrl(data, displayName);

  return raw
    .replace(/\{score\}/g, score)
    .replace(/\{url\}/g, url)
    .replace(/\{stage\}/g, String(data.stage))
    .replace(/\{n\}/g, displayName);
}

export async function copyShareText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textarea);
    return ok;
  }
}

export async function captureCardAsBlob(element: HTMLElement): Promise<Blob | null> {
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(element, {
      backgroundColor: "#0b1f4a",
      scale: 2,
      useCORS: true,
      logging: false,
    });
    return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  } catch {
    return null;
  }
}

export async function shareWithImage(
  element: HTMLElement,
  text: string
): Promise<"shared" | "copied" | "failed"> {
  const blob = await captureCardAsBlob(element);
  if (blob && navigator.share && navigator.canShare) {
    const file = new File([blob], "nigunim.png", { type: "image/png" });
    const shareData = { text, files: [file] };
    if (navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
        return "shared";
      } catch {
        /* user cancelled */
      }
    }
  }
  const ok = await copyShareText(text);
  return ok ? "copied" : "failed";
}
